import { createLogger } from "utils/logging";
import { captureErrorWithPayload } from "utils/error";
import { BenefitElement, defineElement, buildSearchParams, buildFetchHeaders } from "../benefit_base";
import ky from "@otto-ec/global-resources/ky";
import { createDefaultOptions } from "utils/ky";

const ATTRIBUTE_NAMES = {
	FEATURE: "feature",
	TEAM: "team",
	SKU: "sku",
	PARTNER_ID: "partner-id",
};

const log = createLogger("tag", "cashback");
const CASHBACK_ELEMENT_NAME = "ft9-cashback-v1";

const ERROR_KEY = "ft9-cashback-not-ok";
const ERROR_LABELS = { service: "tag", module: "cashback", team: "ft9" };

async function callBackend(url: URL, headers: HeadersInit): Promise<string> {
	const urlSearchParams = url.searchParams.toString();

	try {
		const options = {
			headers,
			priority: "high" as const,
			...createDefaultOptions(log, url.toString()),
		};
		const response = await ky(url, options);
		if (!response.ok) {
			log.error("tag cashback endpoint returns a non ok response!", response.status);
			captureErrorWithPayload({
				message: "callBackend error",
				type: ERROR_KEY,
				labels: ERROR_LABELS,
				custom: { url_search_params: urlSearchParams, tries: 1 },
				payload: response.status,
			});
			return Promise.reject();
		}
		return response.text();
	} catch (error) {
		log.error("tag cashback request returns an error!", error);
		captureErrorWithPayload({
			message: "callBackend error",
			type: ERROR_KEY,
			labels: ERROR_LABELS,
			custom: { url_search_params: urlSearchParams, tries: 3 },
			payload: error,
		});
		return Promise.reject();
	}
}

export class CashbackElement extends BenefitElement {
	private validateHydrationPreconditions(): boolean {
		const sku = this.getAttribute(ATTRIBUTE_NAMES.SKU);
		if (!sku) {
			log.error("No sku provided, cannot load cashback.");
			captureErrorWithPayload({ message: "No sku provided", type: ERROR_KEY, labels: ERROR_LABELS });
			return false;
		}
		const partnerId = this.getAttribute(ATTRIBUTE_NAMES.PARTNER_ID);
		/*                                                                             */
		/*                                                                                     */
		/*                        */
		return partnerId === "1000000" || partnerId === "999999999";
	}

	protected async hydrate(): Promise<void> {
		this.replaceChildren(); /*                       */

		if (!this.validateHydrationPreconditions()) return;

		const params = {
			sku: this.getAttribute(ATTRIBUTE_NAMES.SKU),
			partnerId: this.getAttribute(ATTRIBUTE_NAMES.PARTNER_ID),
		};
		const searchParams = buildSearchParams(params);
		log.info("Calling tag for %s with %s", this.id || "id-not-set", searchParams.toString());

		const url = new URL(`/benefit-tag/cashback?${searchParams}`, globalThis.location.origin);
		try {
			const headers = buildFetchHeaders(this.getAttribute(ATTRIBUTE_NAMES.FEATURE), this.getAttribute(ATTRIBUTE_NAMES.TEAM));
			this.innerHTML = await callBackend(url, headers);
			this.initCashback();
		} catch {
			/*                                                                    */
			/*                                                                          */
		}
	}

	private initCashback() {
		const cashback = this.firstElementChild;
		if (!cashback || !(cashback instanceof HTMLElement)) {
			return;
		}

		if (cashback.dataset.expirationDate) {
			this.triggerReloadAt(cashback.dataset.expirationDate);
		}
	}
}

defineElement(CashbackElement, CASHBACK_ELEMENT_NAME, "ftnine_emergency_custom_element_disable_cashback");
